import { DOCUMENT } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, effect, inject, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, NavigationStart, Router, RouterOutlet } from '@angular/router';
import { UUID_REGEX } from '@common/constants';
import { BrowserStorage, RoutePath } from '@common/enums';
import { LocalStorageService } from '@common/services';
import { AuthSessionService } from '@common/services/auth/auth-session.service';
import { AuthService } from '@common/services/auth/auth.service';
import { DomainStateService } from '@common/services/domain/domain-state.service';
import { hideBootstrapLoader } from '@common/utils';
import { SidepanelService } from 'mbs-ui-kit';
import { Observable, switchMap } from 'rxjs';
import { filter, tap } from 'rxjs/operators';

@Component({
  imports: [RouterOutlet],
  selector: 'app-root',
  template: '<router-outlet />'
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly authService = inject(AuthService);
  private readonly authSession = inject(AuthSessionService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly document = inject(DOCUMENT);
  private readonly domainState = inject(DomainStateService);
  private readonly router = inject(Router);
  private readonly sidepanelService = inject(SidepanelService);
  private readonly storageService = inject(LocalStorageService);

  protected readonly loggedIn = this.authSession.loggedIn;

  protected readonly logoutEffect = effect(() => this.loggedIn() === false && this.domainState.resetCache());

  ngOnDestroy(): void {
    this.storageService.removeAllListeners();
  }

  ngOnInit(): void {
    this.storageService.listenStorageChange();

    this.storageService.change
      .pipe(
        filter(() => this.document.hidden),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((event) => {
        if (event.key !== BrowserStorage.Prefix + BrowserStorage.Token) return;

        if (event.newValue) {
          this.authSession.setAuthenticated(true);
          void this.router.navigate([this.storageService.get<string>(BrowserStorage.AuthPrefix) + RoutePath.Dashboard]);
        } else {
          this.authService.logout(
            new HttpErrorResponse({
              status: 440,
              error: { message: 'Authentication state changed unexpectedly' }
            })
          );
        }
      });

    this.router.events
      .pipe(
        tap((event) => event instanceof NavigationStart && this.handleNavigationStart(event)),
        filter((event) => event instanceof NavigationEnd),
        tap(() => requestAnimationFrame(() => hideBootstrapLoader(this.document))),
        switchMap(() => this.handleNavigationEnd()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private handleNavigationStart(event: NavigationStart): void {
    this.isUrlChanged(event) && this.sidepanelService.remove('my_account');
  }

  private handleNavigationEnd(): Observable<boolean> {
    // Don't close myAccountSidepanel after change user in user-select
    return this.sidepanelService.closeAll('my_account');
  }

  private isUrlChanged(event: NavigationStart): boolean {
    const currentUrl = this.getBaseUrl(this.router.url) ?? '';
    const nextUrl = this.getBaseUrl(event?.url) ?? '';

    return currentUrl !== nextUrl;
  }

  private getBaseUrl(url: string): string {
    if (!url) return '';

    const urlTree = this.router.parseUrl(url);

    urlTree.queryParams = {};
    urlTree.fragment = null;

    const segments = urlTree.toString().split('/').filter(Boolean) || [];
    const cleanedSegments = segments.map((seg) => (this.isDynamicId(seg) ? ':id' : seg));

    return '/' + cleanedSegments.join('/');
  }

  private isDynamicId(segment: string): boolean {
    const numberRegex = /^\d+$/;

    return UUID_REGEX.test(segment) || numberRegex.test(segment);
  }
}
